/**
 * THE FLOOR (doc 06, depth 3) — the last rung, and the only depth whose water
 * is effectively black.
 *
 * That changes the job. On THE SHELF a body is read against a lit seabed and
 * the outline does the separating; down here the outline is invisible and the
 * BODY is the only thing there is, so every shape in this file is built to be
 * legible as a pale mass with no help: a hump, a ribbon, a segmented barrel, a
 * standing column, a knot, a trailing tangle and a crown.
 */

import type { CreatureDraw } from '../creature.ts';
import { bodyLean, gait, tailThrow } from '../creature.ts';
import { blob, dome, fan, legs, step, swell, teeth } from '../organic.ts';

/**
 * TROGLODONT — a slab that heals faster than your excuses.
 *
 * Eyeless, and the only body here with a second mass stacked on its shoulders.
 * Its regeneration used to be three sacs drawn into the back; it is now the
 * universal mote marker, so the Mother of Threads regenerates in the same
 * visual language and a player learns the word once.
 */
export const drawTroglodont: CreatureDraw = (s, c, ctx, p) => {
  const lunge = ctx.state === 'attack' ? 2 : 0;
  blob(s, c.ax - 2 + lunge, c.ay - 1, 11, 5, p.ramp.back, p.ramp.belly);
  for (let k = 0; k < 5; k++) {
    s.hline(c.ax - 7 + k, c.ax + 1 - k, c.ay - 6 - k, k % 2 ? p.ramp.belly : p.ramp.back, 'set');
  }
  s.fillRect(c.ax + 7 + lunge, c.ay - 2, 4, 4, p.ramp.back, 'set');
  if (ctx.state === 'attack') {
    s.fillRect(c.ax + 11, c.ay - 1, 3, 3, p.ramp.maw, 'set');
    teeth(s, c.ax + 11, c.ay - 2, 3, 1, p.ramp.bone);
  } else {
    s.px(c.ax + 10, c.ay, p.ramp.maw);
  }
  legs(s, c.ax, c.ay + 4, [-10, -5, 0, 5], 3, 3, ctx.phase, p.ramp.fin, gait(ctx));
  s.px(c.ax + 6, c.ay - 3, p.glow); // no eyes; this is a sensory pit
};

/**
 * PHANTOM EEL — a ribbon with a fringe.
 *
 * The thinnest, sharpest thing in the water: two rows of body and a dorsal
 * fringe, so at 32 px black it is a line where the Hagfish is a tube. Cloaked
 * and Swift both, which means the player mostly meets it as a shimmer with a
 * wake, and the wake arrives before the fish does.
 */
export const drawPhantomEel: CreatureDraw = (s, c, ctx, p) => {
  const RIBBON = [0, -1, -2, -1, 0, 1, 2, 1];
  const shift = step(ctx.phase, 8) * (ctx.state === 'idle' ? 1 : 2);
  const strike = ctx.state === 'attack';
  for (let k = -13; k <= 8; k++) {
    const off = strike ? 0 : RIBBON[(((k + shift) % 8) + 8) % 8]!;
    s.vline(c.ax + k, c.ay - 1 + off, c.ay + off, k % 2 ? p.ramp.back : p.ramp.belly, 'set');
    if (k % 3 === 0) s.px(c.ax + k, c.ay - 2 + off, p.ramp.fin);
  }
  s.fillRect(c.ax + 9, c.ay - 1, 3, 2, p.ramp.back, 'set');
  if (strike) {
    s.fillRect(c.ax + 12, c.ay - 2, 2, 4, p.ramp.maw, 'set');
    teeth(s, c.ax + 12, c.ay - 3, 2, 1, p.ramp.bone);
  }
  s.px(c.ax + 11, c.ay - 1, p.glow);
};

/**
 * BULWARK ISOPOD — the Shellback's terrifying uncle.
 *
 * Fourteen legs, no permit. The segmentation is its shape rather than its
 * armour marker: a barrel of plates with legs down both sides and antennae out
 * front, where the Shellback is a smooth dome half its size. Doc 06 sends it at
 * your walls, so the attack pose is the front end levered up against one.
 */
export const drawIsopod: CreatureDraw = (s, c, ctx, p) => {
  const rear = ctx.state === 'attack' ? 3 : 0;
  dome(s, c.ax, c.ay - rear, 13, 6, p.ramp.back, p.ramp.belly, 0.66);
  // seven segment ridges: the silhouette's own texture, not a property marker
  for (let k = -9; k <= 9; k += 3) s.px(c.ax + k, c.ay - 6 - rear, p.ramp.fin);
  s.hline(c.ax - 13, c.ax + 12, c.ay + 2 - rear, p.ramp.belly, 'set');
  legs(s, c.ax, c.ay + 3, [-10, -6, -2, 2, 6, 10], 2, 4, ctx.phase, p.ramp.fin, gait(ctx));
  s.px(c.ax + 13, c.ay - 4 - rear + tailThrow(ctx), p.ramp.fin);
  s.px(c.ax + 15, c.ay - 5 - rear + tailThrow(ctx), p.ramp.fin);
  if (ctx.state === 'attack') {
    s.fillRect(c.ax + 12, c.ay - 1, 4, 3, p.ramp.maw, 'set');
    teeth(s, c.ax + 11, c.ay - 2, 3, 1, p.ramp.bone);
  }
  s.px(c.ax + 12, c.ay - 2 - rear, p.glow);
};

/**
 * ABYSSAL CHORISTER — the only vertical creature in the game.
 *
 * A standing column wearing a halo of voices. It hangs four tiles behind the
 * front line making everything around it worse (doc 06's support behaviour), so
 * it has to be identifiable at the BACK of a crowd — which is why it is the one
 * animal that is taller than it is wide.
 */
export const drawChorister: CreatureDraw = (s, c, ctx, p) => {
  const rise = tailThrow(ctx);
  const singing = ctx.state === 'attack';
  for (let k = 0; k < 14; k++) {
    const hw = k < 4 ? 2 : k < 10 ? 3 : 2;
    s.hline(c.ax - hw, c.ax + hw - 1, c.ay - 12 + k + rise, k % 2 ? p.ramp.belly : p.ramp.back, 'set');
  }
  // the halo: a ring of voices, wider open while it sings
  const spread = singing ? 2 : 0;
  for (const [dx, dy] of [
    [-5, -11],
    [0, -13],
    [5, -11],
    [-6, -7],
    [6, -7],
  ] as const) {
    const x = c.ax + dx + (dx < 0 ? -spread : dx > 0 ? spread : 0);
    s.px(x, c.ay + dy + rise, (dx + step(ctx.phase, 2)) % 2 === 0 ? p.glow : p.halo);
  }
  for (const dx of [-3, 0, 3]) {
    const sway = [0, 1, 0, -1][step(ctx.phase + Math.abs(dx) / 4, 4)]!;
    s.vline(c.ax + dx, c.ay + 2, c.ay + 8, p.ramp.fin, 'set');
    s.px(c.ax + dx + sway, c.ay + 9, p.halo);
  }
};

/**
 * THREAD NODE — a piece of her, left where it can still see you.
 *
 * Doc 06 gives it speed 0 and dps 0: it is furniture, not fauna, and it is
 * drawn as furniture — pegged to the floor, symmetrical, with the only glow in
 * the bestiary that sits at the TOP of a stalk rather than on a face.
 */
export const drawThreadNode: CreatureDraw = (s, c, ctx, p) => {
  const beat = swell(ctx.phase);
  s.vline(c.ax, c.ay - 4, c.ay + 3, p.ramp.belly, 'set');
  for (const dy of [-3, -1, 1]) {
    s.px(c.ax - 1, c.ay + dy, p.ramp.back);
    s.px(c.ax + 1, c.ay + dy, p.ramp.back);
  }
  s.fillRect(c.ax - 1, c.ay - 6 - beat, 3, 2 + beat, p.glow, 'set');
  s.hline(c.ax - 3, c.ax + 2, c.ay + 4, p.ramp.fin, 'set'); // it is pegged down
};

/**
 * MOTHER OF THREADS (mini-boss) — she does not heal, she is still arriving.
 *
 * The six filaments streaming behind her ARE the silhouette and they are also
 * the fight: doc 06 has her drop them as nodes, each worth another 15 HP/s. So
 * they trail long, they sway on their own phases, and each carries her light.
 */
export const drawMother: CreatureDraw = (s, c, ctx, p) => {
  const reach = ctx.state === 'attack' ? 2 : 0;
  blob(s, c.ax + 3 + reach, c.ay - 1, 10, 7, p.ramp.back, p.ramp.belly);
  for (let k = 0; k < 7; k++) {
    s.vline(c.ax - 3 + k * 2, c.ay - 9 - (k % 2), c.ay - 7, p.ramp.back, 'set');
  }
  // the six threads, each on its own phase
  for (let k = 0; k < 6; k++) {
    const dy = Math.round(-6 + k * 2.4);
    const sway = [0, 1, 0, -1][step(ctx.phase + k / 4, 4)]! * (ctx.state === 'idle' ? 1 : 2);
    for (let t = 0; t < 13; t++) {
      s.px(c.ax - 7 - t, c.ay + dy + (t > 4 ? sway : 0), t % 2 ? p.ramp.fin : p.halo);
    }
  }
  const gape = ctx.state === 'attack' ? 4 : 2;
  s.fillRect(c.ax + 11 + reach, c.ay - gape + 1, 5, gape * 2 - 2, p.ramp.maw, 'set');
  s.px(c.ax + 14 + reach, c.ay - gape, p.glow);
  s.px(c.ax + 14 + reach, c.ay + gape - 1, p.glow);
};

/**
 * THE HADAL SOVEREIGN (boss) — it has been here longer than the water.
 *
 * The widest, tallest, most plated silhouette in the ocean, and the only one
 * wearing a crown. The four pressure vents that put your grid out every twenty
 * seconds are on the sprite, lit, because doc 06's rule is that a gimmick the
 * player must respond to is visible before it fires.
 */
export const drawSovereign: CreatureDraw = (s, c, ctx, p) => {
  const beat = tailThrow(ctx);
  const lean = bodyLean(ctx);
  const pulsing = ctx.state === 'attack';
  blob(s, c.ax - 2 + lean, c.ay - 1, 17, 9, p.ramp.back, p.ramp.belly);
  // the crown: nine spines, tallest in the middle
  for (let k = 0; k < 9; k++) {
    const h = 4 + (4 - Math.abs(k - 4));
    s.vline(c.ax - 12 + k * 3, c.ay - 10 - h, c.ay - 9, k % 2 ? p.ramp.back : p.ramp.belly, 'set');
  }
  // the vents that end your brownout argument
  for (const [dx, dy] of [
    [-14, -4],
    [-14, 3],
    [12, -5],
    [12, 4],
  ] as const) {
    const lit = pulsing || step(ctx.phase, 2) === 0;
    s.fillRect(c.ax + dx, c.ay + dy, 2, 2, lit ? p.glow : p.halo, 'set');
  }
  const gape = pulsing ? 6 : 4;
  s.fillRect(c.ax + 13, c.ay - gape, 7, gape * 2, p.ramp.maw, 'set');
  teeth(s, c.ax + 14, c.ay - gape, 4, 2, p.ramp.bone);
  teeth(s, c.ax + 15, c.ay + gape - 1, 4, 2, p.ramp.bone);
  s.px(c.ax + 11, c.ay - 7, p.glow);
  s.px(c.ax + 8, c.ay - 8, p.glow);
  fan(s, c.ax - 14, c.ay, 8, 8, beat, p.ramp.fin);
};
